import { PublicKey } from '@solana/web3.js'
import { getConnection } from './connection'
import { getPlayerStatePDA } from './pda'

// ---------------------------------------------------------------------------
// On-chain PlayerState representation
// Mirrors: programs/components/player-state/src/lib.rs -> PlayerState
// ---------------------------------------------------------------------------

export interface OnChainPlayerState {
  authority: PublicKey
  cash: bigint
  drillLevel: number
  fuelTankLevel: number
  cargoLevel: number
  negotiatorLevel: number
  luckLevel: number
  totalRuns: number
  deepestDepth: number
}

// Anchor adds an 8-byte discriminator (sha256("account:PlayerState")[0..8])
// at the start of every account's data buffer.
const DISCRIMINATOR_SIZE = 8

/**
 * Deserialize a raw PlayerState account buffer into a typed object.
 *
 * Layout (after 8-byte discriminator):
 *   authority:         Pubkey  (32 bytes)
 *   cash:              u64     (8 bytes, little-endian)
 *   drill_level:       u8      (1 byte)
 *   fuel_tank_level:   u8      (1 byte)
 *   cargo_level:       u8      (1 byte)
 *   negotiator_level:  u8      (1 byte)
 *   luck_level:        u8      (1 byte)
 *   total_runs:        u32     (4 bytes, little-endian)
 *   deepest_depth:     u32     (4 bytes, little-endian)
 */
export function deserializePlayerState(data: Uint8Array): OnChainPlayerState {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  let offset = DISCRIMINATOR_SIZE

  const authority = new PublicKey(data.slice(offset, offset + 32))
  offset += 32

  const cash = view.getBigUint64(offset, true)
  offset += 8

  const drillLevel = view.getUint8(offset++)
  const fuelTankLevel = view.getUint8(offset++)
  const cargoLevel = view.getUint8(offset++)
  const negotiatorLevel = view.getUint8(offset++)
  const luckLevel = view.getUint8(offset++)

  const totalRuns = view.getUint32(offset, true)
  offset += 4

  const deepestDepth = view.getUint32(offset, true)
  offset += 4

  return {
    authority,
    cash,
    drillLevel,
    fuelTankLevel,
    cargoLevel,
    negotiatorLevel,
    luckLevel,
    totalRuns,
    deepestDepth,
  }
}

/**
 * Fetch the PlayerState account for the given authority.
 * Returns null if the account has not been initialized yet.
 */
export async function fetchPlayerState(
  authority: PublicKey
): Promise<OnChainPlayerState | null> {
  const connection = getConnection()
  const [pda] = getPlayerStatePDA(authority)

  const accountInfo = await connection.getAccountInfo(pda)
  if (!accountInfo) return null

  return deserializePlayerState(accountInfo.data as Uint8Array)
}

/**
 * Subscribe to real-time changes on a PlayerState account via
 * websocket (onAccountChange). Returns a subscription ID that
 * should be cleaned up with unsubscribePlayerState().
 */
export function subscribePlayerState(
  authority: PublicKey,
  callback: (state: OnChainPlayerState) => void
): number {
  const connection = getConnection()
  const [pda] = getPlayerStatePDA(authority)

  return connection.onAccountChange(pda, (accountInfo) => {
    const state = deserializePlayerState(accountInfo.data as Uint8Array)
    callback(state)
  })
}

/** Remove a PlayerState account subscription. */
export function unsubscribePlayerState(subscriptionId: number): void {
  const connection = getConnection()
  connection.removeAccountChangeListener(subscriptionId)
}
